/**
 * Done tab — everything Jess closed, in one place.
 *
 * Scott (2026-09-08): "I see triage cases right away, but I don't see the
 * Done category." Closed threads had been parked as the third section of
 * the Routed away tab (below the fold, uncounted in its badge). Here they
 * get their own home: real issues marked Done on top, dismissals with their
 * calibration reasons below. Rows open the thread's workspace, whose
 * closed-thread action bar offers Reopen (back to the queue) and Open a case
 * (straight into Cases — Scott's second ask that day).
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ageLabel, hasUnseenUpdate, seenSnapshot } from '../../components/portal/threadTriageView';
import { formatInvestigationDate } from '../../components/portal/investigationsView';
import type { DoorInvestigationRead, DoorThreadRead } from '../../services/qcpInvestigations';
import {
  caseMatchesSearch,
  closedLine,
  completedCases,
  completedThreads,
  dismissedThreads,
  hasPostClosureNote,
  postClosureLine,
  threadMatchesSearch,
} from './qualityTriageView';
import { caseWorkspacePath, fromTab, threadWorkspacePath } from './qualityWorkspaceView';
import { useQualityTriage } from './qualityTriageContext';
import { SkipToItems } from './SkipToItems';
import CaseListRow from './CaseListRow';
import { ThreadRefLabel } from './ThreadRefLabel';
import styles from './QualityTriagePages.module.css';

function ClosedRow({
  thread,
  onOpen,
}: {
  thread: DoorThreadRead;
  onOpen: (thread: DoorThreadRead) => void;
}) {
  const dismissed = thread.status === 'dismissed';
  return (
    <button
      type="button"
      className={`${styles.row} ${dismissed ? styles.rowDimmed : ''}`}
      onClick={() => onOpen(thread)}
      data-door-item={thread.thread_no}
      data-action="qcp_item_open"
      data-target-thread-id={thread.thread_no}
    >
      <span className={`${styles.sev} ${styles.sev_watch}`}>
        {dismissed ? 'DISMISSED' : 'DONE'}
      </span>
      <span className={styles.rowBody}>
        <span className={styles.rowTitle}>{thread.title}</span>
        <span className={styles.rowSub}>{closedLine(thread)}</span>
        {hasPostClosureNote(thread) && (
          <span className={styles.rowReopenLine} title={postClosureLine(thread)}>
            {postClosureLine(thread)}
          </span>
        )}
        <span className={styles.chips}>
          {hasPostClosureNote(thread) && (
            <span className={styles.chipNote}>
              Note after closure ·{' '}
              {ageLabel(thread.post_closure_update_at ?? thread.updated_at)}
            </span>
          )}
        </span>
      </span>
      <span className={styles.rowRight}>
        <ThreadRefLabel thread={thread} />
      </span>
    </button>
  );
}

function CaseRow({
  investigation,
  onOpen,
}: {
  investigation: DoorInvestigationRead;
  onOpen: (inv: DoorInvestigationRead) => void;
}) {
  const when = investigation.completed_at ?? investigation.updated_at;
  // First real sentence of the findings (skip a lead-in like "Merlin's read …:").
  const line =
    investigation.findings
      .split('\n')
      .map((l) => l.trim())
      .find((l) => l && !l.endsWith(':')) ?? '';
  const firstFinding = line.length > 140 ? `${line.slice(0, 139)}…` : line;
  return (
    <CaseListRow investigation={investigation} badge="CASE" onOpen={onOpen}>
      <span className={styles.rowSub}>
        Completed {formatInvestigationDate(when)}
        {firstFinding ? ` — ${firstFinding}` : ''}
      </span>
    </CaseListRow>
  );
}

export default function QualityDonePage() {
  const { invState } = useQualityTriage();
  const navigate = useNavigate();

  // Find a past problem by customer, product, lot or number — across closed
  // items AND completed cases, and inside what was written on them.
  const [query, setQuery] = useState('');
  const q = query.trim();
  // `?notes=new` (the tab's "updated after closing" badge, or the button
  // below): only the closed items with news added after closing that have
  // not been read yet — the same ones the badge counts (fan-out finding 10).
  const [params, setParams] = useSearchParams();
  const onlyNotes = params.get('notes') === 'new';
  const seen = seenSnapshot();
  const hasNewNote = (t: DoorThreadRead) => hasPostClosureNote(t) && hasUnseenUpdate(t, seen);
  const newNoteCount = [
    ...completedThreads(invState.threads),
    ...dismissedThreads(invState.threads),
  ].filter(hasNewNote).length;
  const showNotesOnly = (on: boolean) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (on) next.set('notes', 'new');
        else next.delete('notes');
        return next;
      },
      { replace: true },
    );
  const allCases = onlyNotes ? [] : completedCases(invState.investigations);
  const cases = allCases.filter((i) => caseMatchesSearch(i, q));
  const done = completedThreads(invState.threads)
    .filter((t) => threadMatchesSearch(t, q))
    .filter((t) => !onlyNotes || hasNewNote(t));
  const dismissed = dismissedThreads(invState.threads)
    .filter((t) => threadMatchesSearch(t, q))
    .filter((t) => !onlyNotes || hasNewNote(t));
  // Open cases join the results while searching, so a lot or customer that
  // is still under investigation is not missed (turn 4: searching G668869
  // found two closed cases but not the open one on the same lot).
  const openCases = q && !onlyNotes
    ? invState.investigations.filter(
        (i) => (i.status === 'investigating' || i.status === 'remediating') && caseMatchesSearch(i, q),
      )
    : [];
  const hits = cases.length + done.length + dismissed.length + openCases.length;

  // The items as this page lists them: an opened item offers previous /
  // next through them (case round, finding 6).
  const order = [
    ...openCases.map((i) => i.investigation_no),
    ...cases.map((i) => i.investigation_no),
    ...done.map((t) => t.thread_no),
    ...dismissed.map((t) => t.thread_no),
  ];
  const openThread = (thread: DoorThreadRead) =>
    navigate(threadWorkspacePath(thread, 'tab'), fromTab('done', order));
  const openCase = (inv: DoorInvestigationRead) =>
    navigate(caseWorkspacePath(inv, 'tab'), fromTab('done', order));

  return (
    <div className={styles.page}>
      <SkipToItems list="done" />
      <p className={styles.legend}>
        Everything closed: completed cases, items marked Done, and dismissals
        with their reason. Open one to read how it ended or to reopen it.
      </p>

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search cases and closed items: customer, product, lot, number…"
        aria-label="Search closed items"
        style={{
          width: '100%', maxWidth: 560, padding: '8px 12px', margin: '4px 0 8px',
          border: '1px solid var(--cm-gray-300, #d1d5db)', borderRadius: 8, font: 'inherit',
        }}
      />
      {onlyNotes ? (
        <p className={styles.filterNote} role="status">
          <span>
            Showing only the {newNoteCount} closed item{newNoteCount === 1 ? '' : 's'} with news
            added after closing that you have not opened yet.
          </span>
          <button type="button" className={styles.filterBtn} onClick={() => showNotesOnly(false)}>
            Show everything
          </button>
        </p>
      ) : (
        newNoteCount > 0 && (
          <p className={styles.filterNote}>
            <span>
              {newNoteCount} closed item{newNoteCount === 1 ? ' was' : 's were'} updated after
              closing.
            </span>
            <button type="button" className={styles.filterBtn} onClick={() => showNotesOnly(true)}>
              Show only {newNoteCount === 1 ? 'that one' : 'those'}
            </button>
          </p>
        )
      )}
      {q && (
        <p className={styles.emptyNote}>
          {hits === 0
            ? `Nothing closed mentions “${q}”.`
            : `Found ${hits} for “${q}”.`}
        </p>
      )}

      {openCases.length > 0 && (
        <>
          <h2 className={styles.lane}>
            Still open
            <span className={styles.lanePill}>{openCases.length}</span>
          </h2>
          {openCases.map((inv) => (
            <CaseListRow key={inv.id} investigation={inv} badge="OPEN" onOpen={openCase} />
          ))}
        </>
      )}

      {allCases.length > 0 && (!q || cases.length > 0) && (
        <>
          <h2 className={styles.lane}>
            Completed cases
            {cases.length > 0 && <span className={styles.lanePill}>{cases.length}</span>}
          </h2>
          {cases.map((inv) => (
            <CaseRow key={inv.id} investigation={inv} onOpen={openCase} />
          ))}
        </>
      )}

      {/* While searching, a section with no hits drops out instead of
          leaving an empty heading. */}
      {(!(q || onlyNotes) || done.length > 0) && (
        <h2 className={styles.lane}>
          Done — real issue, handled
          {done.length > 0 && <span className={styles.lanePill}>{done.length}</span>}
        </h2>
      )}
      {done.length === 0 && !q && !onlyNotes && <p className={styles.emptyNote}>Nothing marked Done yet.</p>}
      {done.map((t) => (
        <ClosedRow key={t.id} thread={t} onOpen={openThread} />
      ))}

      {(!(q || onlyNotes) || dismissed.length > 0) && (
        <h2 className={styles.lane}>
          Dismissed — not an issue
          {dismissed.length > 0 && <span className={styles.lanePill}>{dismissed.length}</span>}
        </h2>
      )}
      {dismissed.length === 0 && !q && !onlyNotes && <p className={styles.emptyNote}>No dismissals yet.</p>}
      {dismissed.map((t) => (
        <ClosedRow key={t.id} thread={t} onOpen={openThread} />
      ))}

    </div>
  );
}
